import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { ToastrService } from 'ngx-toastr';
import { EventManager, EventWithContent } from './event-manager.service';
import { AlertError } from './alert-error.model';
import { TmwToast } from '@shared/components/toast/toast.component';

const SUCCESS_MSG_KEY = 'entity.operation.success';

@Injectable({
  providedIn: 'root',
})
export class AlertErrorService implements OnDestroy {
  errorListener: Subscription;
  httpErrorListener: Subscription;

  constructor(
    private readonly toastr: ToastrService,
    private readonly eventManager: EventManager,
    private readonly translateService: TranslateService
  ) {
    this.errorListener = eventManager.subscribe('error', (response: EventWithContent<unknown> | string) => {
      const errorResponse = (response as EventWithContent<AlertError>).content;
      this.addErrorAlert(errorResponse.message, errorResponse.key, errorResponse.params);
    });

    this.httpErrorListener = eventManager.subscribe('httpError', (response: EventWithContent<unknown> | string) => {
      const httpErrorResponse = (response as EventWithContent<HttpErrorResponse> | any).content;
      if (!(httpErrorResponse?.status || httpErrorResponse?.status === 0) && httpErrorResponse?.message) {
        this.addErrorAlert(httpErrorResponse.message, 'error');
      } else {
        switch (httpErrorResponse.status) {
          case 0:
            // connection refused, server not reachable
            this.addErrorAlert('Server not reachable', 'error.server.not.reachable');
            break;
          case 404:
            this.addErrorAlert('Not found', 'http.error');
            break;
          case 500:
            // Vérifier d'abord si un message spécifique est disponible
            if (httpErrorResponse?.error?.message) {
              this.addErrorAlert('Erreur', httpErrorResponse.error.message);
            } else {
              this.addErrorAlert('Contact your admin', 'http.error');
            }
            break;
          case 502:
            this.addErrorAlert('Bad Gateway', 'error.server.bad.gateway');
            break;
          case 503:
            this.addErrorAlert('Service temporarily unavailable', 'error.server.unavailable');
            break;
          case 504:
            this.addErrorAlert('Gateway Timeout', 'error.server.timeout');
            break;
          case 401:
            this.addErrorAlert('Votre session a expiré. Veuillez vous reconnecter pour continuer', 'http.error');
            break;
          case 400:
          default:
            let title = `http.error`;
            //let title = `http.error.${httpErrorResponse.status}`;
            if (httpErrorResponse?.error?.message) {
              this.addErrorAlert(title, httpErrorResponse?.error?.message);
            } else if (httpErrorResponse?.error?.properties?.description) {
              // Format ProblemDetail JHipster (ex: BadRequestAlertException) : le message utile
              // est dans properties.description, pas dans le champ "detail" generique.
              this.addErrorAlert(title, httpErrorResponse.error.properties.description);
            } else if (typeof httpErrorResponse?.error?.detail === 'string' && httpErrorResponse.error.detail) {
              // ProblemDetail Spring standard (ex: 409 des @RestControllerAdvice de violation d'unicite) :
              // le message metier est dans "detail".
              this.addErrorAlert(title, httpErrorResponse.error.detail);
            } else if (typeof httpErrorResponse?.error?.error === 'string' && httpErrorResponse.error.error) {
              // Format {"error": "..."} (ex: upload BPMN du Workflow) : le message metier est dans "error".
              this.addErrorAlert(title, httpErrorResponse.error.error);
            } else if (httpErrorResponse?.error?.errorDetails?.length) {
              // let detail =  httpErrorResponse.error.errorDetails[0];
              // this.addErrorAlert(title, detail.errorMessage);
              this.addErrorAlert(title, this.getErrorMessage(httpErrorResponse.error));
            } else if (httpErrorResponse?.error?.errors) {
              let query = httpErrorResponse.error.errors.query;
              let detail = httpErrorResponse.error?.errors.$;
              let msg = '';
              if (query?.length) {
                title = query[0];
              }
              if (detail?.length) {
                msg = detail[0];
              }
              this.addErrorAlert(title, msg);
            } else if (httpErrorResponse?.error !== '') {
              this.addErrorAlert(title, httpErrorResponse?.error);
              // this.addErrorAlert(title, httpErrorResponse.error.errorDetails[0].errorMessage);
            } else {
              this.addErrorAlert(title, httpErrorResponse?.error?.code ?? `http.error.${httpErrorResponse.status}.message`);
            }
        }
      }
    });
  }

  getErrorMessage(error: any): string {
    let res = '';
    if (error?.errorDetails?.length) {
    }

    return res;
  }

  ngOnDestroy(): void {
    this.eventManager.destroy(this.errorListener);
    this.eventManager.destroy(this.httpErrorListener);
  }

  private addErrorAlert(title: string, messageKey?: string, translationParams?: { [key: string]: unknown }): void {
    if (messageKey && typeof messageKey !== 'string') {
      // Ne doit jamais arriver, mais evite un TypeError silencieux (messageKey.includes)
      // qui casse tout le toast si un appelant passe un objet par erreur.
      this.toastr.error('http.error', '', { timeOut: 2000, toastComponent: TmwToast });
      return;
    }
    if (messageKey) {
      if (messageKey.includes(' ')) {
        this.toastr.error(messageKey, '', { timeOut: 2000, toastComponent: TmwToast });
      } else {
        this.toastr.error(messageKey, '', {
          timeOut: 2000,
          toastComponent: TmwToast,
          payload: { translationParams },
        });
      }
    }
  }

  showOperationSuccess() {
    this.toastr.success(SUCCESS_MSG_KEY, undefined, {
      timeOut: 2000,
      toastComponent: TmwToast,
      positionClass: 'toast-top-center',
    });
  }

  showSuccess(messageKey: string, titleKey?: string) {
    if (titleKey) {
      this.toastr.success(titleKey, messageKey, { timeOut: 2000 });
    } else {
      this.toastr.success(messageKey, undefined, { timeOut: 2000 });
    }
  }
  showError(messageKey: string, titleKey?: string) {
    if (titleKey) {
      this.toastr.error(titleKey, messageKey, { timeOut: 2000, toastComponent: TmwToast });
    } else {
      this.toastr.error(messageKey, undefined, { timeOut: 2000, toastComponent: TmwToast });
    }
  }
  showErrorWithParam(messageKey: string, titleKey?: string, translationParams?: { [key: string]: unknown }) {
    if (titleKey) {
      this.toastr.error(messageKey, titleKey, {
        timeOut: 2000,
        toastComponent: TmwToast,
        payload: { translationParams },
      });
    } else {
      this.toastr.error(messageKey, undefined, {
        timeOut: 2000,
        toastComponent: TmwToast,
        payload: { translationParams },
      });
    }
  }
  showWarning(messageKey: string, titleKey?: string) {
    if (titleKey) {
      this.toastr.warning(titleKey, messageKey, { timeOut: 2000 });
    } else {
      this.toastr.warning(messageKey, undefined, { timeOut: 2000 });
    }
  }
}
